import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button, Callout, EmptyState, Heading, IconButton, Paginator, PropertyList, PropertyListItem,
  SelectField, Spinner, Splitter, SplitterPanel, Table, Tag, TextField, type TableColumn,
} from '@humanforce/mango-ui-react';
import { browseBsbDirectory, getBsbDirectoryDetail } from './bsbDirectoryHttpClient';
import type {
  BsbDirectoryBrowseResult, BsbDirectoryRequestError, BsbDirectoryRow,
  BsbDirectorySortDirection, BsbDirectorySortField, BsbDirectoryStatus, TenantBsbDirectoryDetail,
} from './bsbDirectoryApiTypes';
import {
  BSB_DIRECTORY_PAGE_SIZE, addressLines, displayValue, electronicOptions, locationLabel,
  provenanceLabel, stateOptions, statusLabels, statusOptions, statusTones,
} from './bsbDirectoryModel';
import './BsbDirectoryScreen.css';

const isRequestError = (error: unknown): error is BsbDirectoryRequestError =>
  typeof error === 'object' && error !== null && 'status' in error;

const errorMessage = (error: unknown) =>
  isRequestError(error) ? error.message : 'The BSB Directory could not be loaded.';

/** 503 is the Admin-unavailable signal; anything else is an ordinary request failure. */
const isUnavailable = (error: unknown) => isRequestError(error) && error.status === 503;

/**
 * PRR-325 tenant BSB Directory.
 *
 * The grid is the primary surface. Selecting a row opens the Mango canonical
 * Contextual Panel (Splitter 65/35) alongside it — Mango ships no drawer or
 * side-sheet overlay, and its guidance rejects one because a side overlay makes
 * the list unusable while open.
 *
 * Grid state (search, filters, sort, page) lives above the panel and is never
 * reset by opening or closing it, so closing returns the operator to exactly the
 * view they left.
 */
export const BsbDirectoryScreen = () => {
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [status, setStatus] = useState('');
  const [electronic, setElectronic] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<BsbDirectorySortField>('bsb');
  const [sortDirection, setSortDirection] = useState<BsbDirectorySortDirection>('asc');

  const [result, setResult] = useState<BsbDirectoryBrowseResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [selected, setSelected] = useState<BsbDirectoryRow | null>(null);
  const [detail, setDetail] = useState<TenantBsbDirectoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  // A 404 is an authoritative answer from the directory, not an outage, and must not
  // present as one. The tenant API can now return it distinctly.
  const [detailMissing, setDetailMissing] = useState(false);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const detailRequest = useRef(0);
  const detailAbort = useRef<AbortController | null>(null);

  // Debounced so typing does not issue a request per keystroke. Filter and sort
  // changes ride the same effect; the 250ms wait is imperceptible for them.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      void (async () => {
        try {
          const response = await browseBsbDirectory({
            search: search.trim() || undefined,
            state: state || undefined,
            status: (status || undefined) as BsbDirectoryStatus | undefined,
            isElectronic: electronic === '' ? undefined : electronic === 'true',
            page,
            pageSize: BSB_DIRECTORY_PAGE_SIZE,
            sortBy,
            sortDirection,
          }, controller.signal);
          setResult(response);
          setError('');
          setUnavailable(false);
        } catch (caught) {
          if (controller.signal.aborted) return;
          // An outage must never be rendered as an empty directory.
          setResult(null);
          setUnavailable(isUnavailable(caught));
          setError(errorMessage(caught));
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      })();
    }, 250);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [electronic, page, reloadToken, search, sortBy, sortDirection, state, status]);

  // Reset to the first page whenever the result set changes shape.
  useEffect(() => { setPage(1); }, [electronic, search, sortBy, sortDirection, state, status]);

  // Move focus into the panel when it opens, per the Contextual Panel pattern.
  useEffect(() => {
    if (!selected) return;
    const frame = requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])')?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [selected]);

  const closePanel = useCallback(() => {
    const previous = selected?.bsb;
    setSelected(null);
    setDetail(null);
    setDetailError('');
    setDetailMissing(false);
    detailRequest.current += 1;
    detailAbort.current?.abort();
    detailAbort.current = null;
    // The Contextual Panel pattern requires rendering only the primary pane when
    // nothing is selected, so the grid subtree is recreated on close and a captured
    // node reference would be detached. Re-find the row trigger by BSB instead.
    requestAnimationFrame(() => {
      if (previous) document.querySelector<HTMLElement>(`[data-bsb-trigger="${previous}"]`)?.focus();
    });
  }, [selected]);

  // Escape closes the panel, as the Contextual Panel pattern requires.
  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closePanel, selected]);

  // Issues the detail request for one BSB. Shared by row selection and the retry action,
  // so an unavailable detail can be recovered in place rather than by closing and
  // reopening the panel — which would also lose the operator's place in the grid.
  const loadDetail = useCallback((bsb: string) => {
    const request = ++detailRequest.current;
    detailAbort.current?.abort();
    const controller = new AbortController();
    detailAbort.current = controller;
    setDetail(null);
    setDetailError('');
    setDetailMissing(false);
    setDetailLoading(true);
    void (async () => {
      try {
        const response = await getBsbDirectoryDetail(bsb, controller.signal);
        if (request !== detailRequest.current) return;
        setDetail(response);
      } catch (caught) {
        if (request !== detailRequest.current || controller.signal.aborted) return;
        if (isRequestError(caught) && caught.status === 404) setDetailMissing(true);
        else setDetailError(errorMessage(caught));
      } finally {
        if (request === detailRequest.current) setDetailLoading(false);
      }
    })();
  }, []);

  const openRow = (row: BsbDirectoryRow) => {
    setSelected(row);
    loadDetail(row.bsb);
  };

  useEffect(() => () => detailAbort.current?.abort(), []);

  const columns: TableColumn[] = [
    {
      field: 'bsb', header: 'BSB', sortable: true, style: { width: '8rem' },
      body: (row: BsbDirectoryRow) => (
        <button
          type="button"
          className="bsb-directory__row-trigger"
          data-bsb-trigger={row.bsb}
          onClick={(event) => { event.stopPropagation(); openRow(row); }}
        >{row.bsb}</button>
      ),
    },
    { field: 'institution', header: 'Institution', sortable: true, style: { width: '9rem' } },
    { field: 'branch', header: 'Branch', sortable: true },
    {
      field: 'location', header: 'Location',
      body: (row: BsbDirectoryRow) => locationLabel(row),
    },
    {
      field: 'directoryStatus', header: 'Status', style: { width: '9rem' },
      body: (row: BsbDirectoryRow) => (
        <Tag
          label={row.hasReplacement ? `${statusLabels[row.directoryStatus]} · replaced` : statusLabels[row.directoryStatus]}
          tone={statusTones[row.directoryStatus]}
        />
      ),
    },
    {
      field: 'isElectronic', header: 'Electronic', style: { width: '7rem' },
      body: (row: BsbDirectoryRow) => (row.isElectronic ? 'Yes' : 'No'),
    },
  ];

  const grid = (
    <div className="bsb-directory" data-testid="bsb-directory">
      <div className="mgo-stack mgo-gap-xs">
        <Heading level={2}>BSB Directory</Heading>
        <p>Reference data maintained centrally. Read only.</p>
      </div>

      <div className="bsb-directory__filters">
        <div className="bsb-directory__search">
          <TextField
            label="Search BSB, institution, branch or suburb"
            hideLabel
            placeholder="Search BSB, institution, branch or suburb"
            leadingIcon="search"
            clearable
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            testId="bsb-directory-search"
          />
        </div>
        <div className="bsb-directory__filter">
          <SelectField label="State" options={stateOptions} value={state} searchable={false}
            onChange={(value) => setState((value ?? '') as string)} testId="bsb-directory-state" />
        </div>
        <div className="bsb-directory__filter">
          <SelectField label="Status" options={statusOptions} value={status} searchable={false}
            onChange={(value) => setStatus((value ?? '') as string)} testId="bsb-directory-status" />
        </div>
        <div className="bsb-directory__filter">
          <SelectField label="Electronic" options={electronicOptions} value={electronic} searchable={false}
            onChange={(value) => setElectronic((value ?? '') as string)} testId="bsb-directory-electronic" />
        </div>
      </div>

      {unavailable || error ? (
        <Callout
          tone="critical"
          title={unavailable ? 'BSB Directory unavailable' : 'BSB Directory could not be loaded'}
          action={<Button label="Retry" variant="secondary" onClick={() => setReloadToken((token) => token + 1)} testId="bsb-directory-retry" />}
        >
          {unavailable
            ? 'The central BSB Directory could not be reached. This is not the same as no BSBs matching your search.'
            : error}
        </Callout>
      ) : null}

      {loading && !result ? (
        <div className="bsb-directory__status" aria-live="polite" aria-busy="true">
          <Spinner /><span>Loading the BSB Directory…</span>
        </div>
      ) : null}

      {result && result.totalItems === 0 && !loading ? (
        <EmptyState
          variant="no-data"
          title="No BSBs match your search"
          description="Try a different BSB, institution, branch or suburb, or clear the filters."
        />
      ) : null}

      {result && result.totalItems > 0 ? (
        <>
          <Table
            value={result.items}
            columns={columns}
            loading={loading}
            onRowClick={openRow}
            sortField={sortBy}
            sortOrder={sortDirection === 'asc' ? 1 : -1}
            // `lazy` delegates sorting to the server; it is the documented way to receive
            // a sort event. Paging stays on the standalone Paginator below, matching the
            // repo convention, so `first`/`totalRecords` are deliberately not passed —
            // the Table renders no paginator of its own.
            lazy
            onLazyLoad={(event) => {
              // Take the authoritative field and order from the event rather than toggling
              // local state. The header cycles asc -> desc -> unsorted; the cleared step must
              // return to the default ordering, otherwise the controlled sortOrder stays at
              // -1 and the column can never be clicked back to ascending.
              if (!event.sortField || !event.sortOrder) {
                setSortBy('bsb');
                setSortDirection('asc');
                return;
              }
              setSortBy(event.sortField as BsbDirectorySortField);
              setSortDirection(event.sortOrder === 1 ? 'asc' : 'desc');
            }}
            emptyMessage="No BSBs match your search."
            testId="bsb-directory-table"
          />
          {result.totalPages > 1 ? (
            <Paginator
              first={(result.page - 1) * result.pageSize}
              itemsPerPage={result.pageSize}
              totalRecords={result.totalItems}
              onPageChange={(event) => setPage(event.page + 1)}
              testId="bsb-directory-paginator"
            />
          ) : null}
        </>
      ) : null}
    </div>
  );

  if (!selected) return grid;

  const provenance = detail ? provenanceLabel(detail.directory) : null;
  const references = detail?.whereReferenced.employerBankAccounts ?? [];
  const referenceTotal = detail?.whereReferenced.totalCount ?? references.length;

  const panel = (
    <div className="bsb-directory__panel" ref={panelRef} role="region" aria-label={`BSB ${selected.bsb} detail`} data-testid="bsb-directory-panel">
      <div className="bsb-directory__panel-header">
        <div className="mgo-stack mgo-gap-xs">
          <Heading level={3}>{selected.bsb}</Heading>
          <span>{selected.institution}</span>
        </div>
        <IconButton icon="close" label="Close panel" onClick={closePanel} testId="bsb-directory-panel-close" />
      </div>

      {detailLoading ? (
        <div className="bsb-directory__status" aria-live="polite" aria-busy="true">
          <Spinner /><span>Loading BSB details…</span>
        </div>
      ) : null}

      {detailMissing ? (
        <Callout tone="caution" title="BSB not found" testId="bsb-directory-detail-not-found">
          This BSB is not in the central directory. It may have been mistyped, or removed from the directory.
        </Callout>
      ) : null}

      {detailError ? (
        <Callout
          tone="critical"
          title="BSB Directory unavailable"
          testId="bsb-directory-detail-unavailable"
          action={<Button label="Retry" variant="secondary" onClick={() => loadDetail(selected.bsb)} testId="bsb-directory-detail-retry" />}
        >{detailError}</Callout>
      ) : null}

      {detail ? (
        <div className="mgo-stack mgo-gap-l">
          {provenance?.isStale ? (
            <Callout tone="caution" title="Directory may be out of date">
              The central directory was last refreshed on {provenance.refreshed}.
            </Callout>
          ) : null}

          <PropertyList>
            <PropertyListItem label="BSB">{detail.directory.bsb}</PropertyListItem>
            <PropertyListItem label="Institution">{displayValue(detail.directory.institution)}</PropertyListItem>
            <PropertyListItem label="Branch">{displayValue(detail.directory.branch)}</PropertyListItem>
            <PropertyListItem label="Address">
              {addressLines(detail.directory).length
                ? <span className="mgo-stack mgo-gap-xs">{addressLines(detail.directory).map((line) => <span key={line}>{line}</span>)}</span>
                : '—'}
            </PropertyListItem>
            <PropertyListItem label="Status">
              <Tag label={statusLabels[detail.directory.directoryStatus]} tone={statusTones[detail.directory.directoryStatus]} />
            </PropertyListItem>
            <PropertyListItem label="Electronic payments">
              {detail.directory.isElectronic ? 'Supported' : 'Not supported'}
            </PropertyListItem>
            {detail.directory.supersededByBsb ? (
              <PropertyListItem label="Replaced by">{detail.directory.supersededByBsb}</PropertyListItem>
            ) : null}
            <PropertyListItem label="Directory refreshed">{provenance?.refreshed}</PropertyListItem>
            <PropertyListItem label="Directory status">{provenance?.status}</PropertyListItem>
          </PropertyList>

          <div className="mgo-stack mgo-gap-s">
            <Heading level={4}>Where referenced</Heading>
            {references.length ? (
              <div className="mgo-stack mgo-gap-xs">
                <span>Employer bank accounts</span>
                <ul data-testid="bsb-directory-references">
                  {references.map((reference) => (
                    <li key={reference.bankAccountId}>{reference.accountNameDisplay} ({reference.accountCode})</li>
                  ))}
                </ul>
                {referenceTotal > references.length ? (
                  <span data-testid="bsb-directory-references-truncated">
                    Showing the first {references.length} of {referenceTotal} accounts.
                  </span>
                ) : null}
              </div>
            ) : (
              <p data-testid="bsb-directory-references-empty">
                No current employer bank accounts use this BSB.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );

  // Contextual Panel: 65/35 with the pattern's minimum sizes, and the split
  // persisted so it survives navigation. When nothing is selected only the
  // primary pane renders, so no orphan gutter is left behind.
  return (
    <Splitter stateKey="bsb-directory-panel" stateStorage="local" testId="bsb-directory-splitter" className="bsb-directory__split">
      <SplitterPanel size={65} minSize={40}>
        <div className="bsb-directory__pane">{grid}</div>
      </SplitterPanel>
      <SplitterPanel size={35} minSize={25}>{panel}</SplitterPanel>
    </Splitter>
  );
};
